"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Button,
  Check,
  ChevronDown,
  CircleAlert,
  Eye,
  Icon,
  IconButton,
  Info,
  Minus,
  Navigation,
  Plus,
  RotateCcw,
  Spinner,
  X,
  cx,
} from "@/components";
// Deep imports of the BROWSER-SAFE engine modules — the "@/lib/nl" barrel also
// re-exports server-only pieces (caps, logging → pg), which can't enter a
// client bundle. client.ts + opstack.ts + copy.ts are the pure, LLM-free glue.
import {
  applyNLResponse,
  buildNLRequestBody,
  dispatchEffect,
  reRunQuery,
  type NLApiResponse,
  type NLApplyResult,
} from "@/lib/nl/client";
import { nlCopy } from "@/lib/nl/copy";
import { queryProvenance } from "@/lib/nl/provenance";
import type { FilterNode, MapOp } from "@/lib/contracts/nl";
import type { Anchor, QueryPlan } from "@/lib/nl/types";
import type { MapSpec, PointSet } from "@/lib/map";
import { clientTrack } from "@/lib/analytics/client";
import { createFunnelEvents } from "@/lib/analytics/events";
import "./nlbar.css";

/** A promoted-view request the parent turns into a persisted SavedView (AC-10). */
export interface SaveViewRequest {
  sourceQuestion: string;
  filterPredicate: FilterNode | null;
  matchedIds: string[];
}

export interface NLBarProps {
  /** Map id for the NL request (draft id, or a stable local fallback). */
  mapId: string;
  /** White-label brand name (server-resolved) — the NL copy is derived from it. */
  brandName: string;
  /** Analytics identity for the M5 NL funnel events. */
  anonSessionId: string;
  /**
   * Seed text for the input (FAIL-440) — the question typed into the dashboard/
   * detail AskBar, threaded through as `?prompt=`. The bar mounts only once the
   * dataset is live, so this prefills (and focuses) the field the moment the map
   * appears; the user reviews and sends. Auto-run is deliberately left to the
   * user's Enter — see the mount effect below.
   */
  initialInput?: string;
  pointSet: PointSet;
  /** The spec BENEATH the NL op stack — the GUI-authored base of the fold. */
  baseSpec: MapSpec;
  /** The current NL op stack (rendered spec = reduceStack(baseSpec, ops)). */
  ops: MapOp[];
  /** STYLE ops landed: parent re-renders result.spec and stores result.ops. */
  onStyle: (result: NLApplyResult, baseUsed: MapSpec) => void;
  /** QUERY effect: row ids to light up on the canvas ([] clears). */
  onHighlight: (ids: string[]) => void;
  /** QUERY effect: on-map annotation badges ([] clears). */
  onAnnotate: (annotations: Array<{ id: string; text: string }>) => void;
  /** QUERY effect: frame the matched features (the `fit` effect). */
  onFit: (ids: string[]) => void;
  /** Promote the current QUERY result to a persisted SavedView (AC-10). */
  onSaveView: (req: SaveViewRequest) => void;
  onUndo: () => void;
  canRedo: boolean;
  onRedo: () => void;
  onReset: () => void;
}

/** Transient result surface above the bar (STYLE has its own persistent row). */
type NLPanel =
  | { kind: "query"; result: NLApplyResult; sourceQuestion: string; saved: boolean }
  | { kind: "clarify"; question: string; options: string[]; prompt: string }
  | { kind: "declined"; text: string; fallbacks: string[] }
  | { kind: "degraded"; text: string }
  | { kind: "error"; text: string };

/** Per-click anchor nudge step, in degrees (~11 km of latitude). */
const ANCHOR_NUDGE_STEP = 0.1;

const HINT_ROTATE_MS = 4200;

/** Rotating placeholder asks, built from the file's OWN column names. */
function buildHints(pointSet: PointSet): string[] {
  const cats = pointSet.columns.filter(
    (c) => (c.type === "text" || c.type === "state" || c.type === "boolean") && c.distinctSample.length >= 2,
  );
  const cat = cats.find((c) => c.distinctSample.length <= 8) ?? cats[0];
  const num = pointSet.columns.find((c) => c.type === "number");
  const hints: string[] = [];
  if (cat) hints.push(`Try: “color the points by ${cat.name}”`);
  if (cat?.distinctSample[0]) hints.push(`Try: “show only ${cat.name} = ${cat.distinctSample[0]}”`);
  if (num && cat) hints.push(`Ask: “which ${cat.name} has the highest ${num.name}?”`);
  else if (num) hints.push(`Ask: “what is the average ${num.name}?”`);
  hints.push("Ask a question, or say how to style the map");
  return hints;
}

/** Row ids a QUERY effect should light up (annotate → its target rows). */
function effectIds(result: NLApplyResult): string[] {
  const e = result.effect;
  if (!e || e.kind === "none") return [];
  if (e.kind === "annotate") return e.annotations.map((a) => a.id);
  return e.ids;
}

/** Compact, readable params line for the "What I did" op list. */
function opArgs(op: MapOp): string {
  const entries = Object.entries(op.params ?? {});
  if (entries.length === 0) return "—";
  return entries
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join("  ·  ");
}

function formatValue(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Four-point spark — the NL signature glyph (concept §01), data-accent coral. */
function Spark() {
  return (
    <svg className="nlbar__spark" width="18" height="18" viewBox="0 0 20 20" aria-hidden>
      <path d="M10 1l1.9 6.1L18 9l-6.1 1.9L10 17l-1.9-6.1L2 9l6.1-1.9L10 1z" fill="var(--color-secondary-default)" />
    </svg>
  );
}

/**
 * The persistent NL bar (M5) — the one loud input, floating on the live map.
 * Submits the prompt + schema-only context to /api/nl, then executes the
 * returned plan LOCALLY via applyNLResponse (rows never leave the browser):
 * STYLE folds ops onto the spec (with Undo/Reset and a read-only "What I did"
 * op list), QUERY shows the answer + interpretation echo and lights up the
 * matched points, CLARIFY asks exactly one question as option chips,
 * OUT_OF_SCOPE declines honestly, and a degraded backend goes calm — the
 * manual controls always keep working.
 */
export function NLBar({
  mapId,
  brandName,
  anonSessionId,
  initialInput,
  pointSet,
  baseSpec,
  ops,
  onStyle,
  onHighlight,
  onAnnotate,
  onFit,
  onSaveView,
  onUndo,
  canRedo,
  onRedo,
  onReset,
}: NLBarProps) {
  const [input, setInput] = useState(initialInput?.trim() ?? "");
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<NLPanel | null>(null);
  const [lastStyleText, setLastStyleText] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  /** Nudged anchor coords while the resolved-anchor editor is open (AC-4). */
  const [anchorEdit, setAnchorEdit] = useState<{ lat: number; lng: number } | null>(null);
  // Ever-incrementing tick; the shown hint is tick mod hints. tick > 0 means
  // rotation is live, so only rotated-in hints wear the fade class.
  const [hintIdx, setHintIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hints = useMemo(() => buildHints(pointSet), [pointSet]);
  const hint = hints[hintIdx % hints.length];

  // White-label copy + the M5 NL analytics funnel (same client sink the M0
  // beacon uses). `nl_query_first` fires once per mounted map (the aha moment).
  const copy = useMemo(() => nlCopy(brandName), [brandName]);
  const analytics = useMemo(() => createFunnelEvents(clientTrack), []);
  const ctx = useMemo(() => ({ anonSessionId, sourceSurface: "editor" as const }), [anonSessionId]);
  const firstFired = useRef(false);
  const mountedAt = useRef(0);
  // The map went live when this bar mounted — the baseline for `nl_query_first`.
  useEffect(() => {
    mountedAt.current = typeof performance !== "undefined" ? performance.now() : 0;
  }, []);

  // FAIL-440: when the bar arrives with an AskBar-seeded prompt, put the cursor
  // in the field (caret at the end) so the question is one Enter away. Mount-only
  // — later edits must never steal focus.
  useEffect(() => {
    if (!initialInput?.trim()) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fireFirst(intent: string) {
    if (firstFired.current) return;
    firstFired.current = true;
    const nowMs = typeof performance !== "undefined" ? performance.now() : 0;
    analytics.nlQueryFirst(ctx, { intent, time_since_first_map_ms: Math.round(nowMs - mountedAt.current) });
  }

  /** Route a QUERY effect to the canvas seams (highlight / badges / fit). */
  function applyQueryEffects(result: NLApplyResult) {
    const d = dispatchEffect(result.effect);
    onHighlight(d.highlightIds);
    onAnnotate(d.annotations);
    if (d.fitIds.length > 0) onFit(d.fitIds);
    return d;
  }

  // Rotate the placeholder through the dataset's own asks; under
  // prefers-reduced-motion the first one stays put (no timer, no fade).
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = window.setInterval(() => setHintIdx((i) => i + 1), HINT_ROTATE_MS);
    return () => window.clearInterval(t);
  }, []);

  async function submit(prompt: string) {
    const q = prompt.trim();
    if (q === "" || busy) {
      if (q === "") inputRef.current?.focus();
      return;
    }
    setBusy(true);
    setAnchorEdit(null);
    analytics.nlQuerySubmitted(ctx, { prompt_len: q.length });
    const baseUsed = baseSpec;
    try {
      const body = buildNLRequestBody(q, mapId, pointSet, {
        identity: { anonSessionId },
        requestId: globalThis.crypto?.randomUUID?.(),
      });
      const res = await fetch("/api/nl", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        analytics.nlQueryFailed(ctx, {
          failure_code: res.status === 429 ? "rate_limited" : "request_failed",
          intent: "UNKNOWN",
        });
        setPanel({ kind: "error", text: res.status === 429 ? copy.rateLimited : copy.requestFailed });
        return;
      }
      const api = (await res.json()) as NLApiResponse;
      const result = applyNLResponse(api, { baseSpec: baseUsed, opStack: ops, pointSet });
      fireFirst(result.intent);
      const hadFallback = (result.fallbacks?.length ?? 0) > 0;

      if (result.degraded) {
        analytics.nlQueryFailed(ctx, { failure_code: "degraded", intent: result.intent });
        setPanel({ kind: "degraded", text: result.answerText });
        return;
      }
      if (result.intent === "CLARIFY_NEEDED" && result.clarify) {
        setPanel({ kind: "clarify", question: result.clarify.question, options: result.clarify.options, prompt: q });
        return;
      }
      if (result.intent === "STYLE" && result.ops.length > ops.length) {
        onStyle(result, baseUsed);
        onHighlight([]);
        onAnnotate([]);
        setLastStyleText(result.answerText !== "" ? result.answerText : null);
        setPanel(null);
        setInput("");
        analytics.nlQuerySucceeded(ctx, {
          intent: "STYLE",
          confidence: api.confidence,
          op_count: result.ops.length,
          had_fallback: hadFallback,
        });
        return;
      }
      if (result.intent === "QUERY") {
        const d = applyQueryEffects(result);
        setPanel({ kind: "query", result, sourceQuestion: q, saved: false });
        setInput("");
        analytics.nlQuerySucceeded(ctx, {
          intent: "QUERY",
          confidence: api.confidence,
          op_count: 0,
          had_fallback: hadFallback,
        });
        const highlighted = d.highlightIds.length + d.annotations.length;
        if (highlighted > 0) analytics.nlAnswerHighlighted(ctx, { highlighted_row_count: highlighted });
        return;
      }
      // OUT_OF_SCOPE (or a STYLE reply that carried no ops): the honest decline.
      analytics.nlQueryFailed(ctx, { failure_code: "out_of_scope", intent: result.intent });
      setPanel({ kind: "declined", text: result.answerText, fallbacks: result.fallbacks ?? [] });
    } catch {
      analytics.nlQueryFailed(ctx, { failure_code: "network", intent: "UNKNOWN" });
      setPanel({ kind: "error", text: copy.networkError });
    } finally {
      setBusy(false);
    }
  }

  /** Re-run the current spatial QUERY with a corrected anchor — LLM-free (AC-4). */
  function rerunWithAnchor(next: { lat: number; lng: number }) {
    if (panel?.kind !== "query") return;
    const q = panel.result.query;
    if (!q?.spatial) return;
    const anchor: Anchor = { ...q.spatial.anchor, lat: next.lat, lng: next.lng };
    const newQuery: QueryPlan = { ...q, spatial: { ...q.spatial, anchor } };
    const result = reRunQuery(newQuery, { baseSpec, opStack: ops, pointSet });
    applyQueryEffects(result);
    setPanel({ kind: "query", result, sourceQuestion: panel.sourceQuestion, saved: false });
  }

  /** Nudge the resolved anchor by one step on an axis, then re-run (AC-4). */
  function nudgeAnchor(dLat: number, dLng: number) {
    if (panel?.kind !== "query" || !panel.result.anchor) return;
    const from = anchorEdit ?? { lat: panel.result.anchor.lat, lng: panel.result.anchor.lng };
    const next = {
      lat: Math.round((from.lat + dLat) * 10000) / 10000,
      lng: Math.round((from.lng + dLng) * 10000) / 10000,
    };
    setAnchorEdit(next);
    rerunWithAnchor(next);
  }

  /** Promote the current QUERY result to a persisted SavedView (AC-10). */
  function saveCurrentView() {
    if (panel?.kind !== "query") return;
    const d = dispatchEffect(panel.result.effect);
    const matchedIds = d.highlightIds.length > 0 ? d.highlightIds : d.annotations.map((a) => a.id);
    onSaveView({
      sourceQuestion: panel.sourceQuestion,
      filterPredicate: (panel.result.query?.filter as FilterNode | undefined) ?? null,
      matchedIds,
    });
    setPanel({ ...panel, saved: true });
  }

  function dismissPanel() {
    if (panel?.kind === "query") {
      onHighlight([]);
      onAnnotate([]);
    }
    setPanel(null);
    setAnchorEdit(null);
  }

  function handleUndo() {
    onUndo();
    setLastStyleText(null);
    if (ops.length <= 1) setExpanded(false);
  }

  function handleReset() {
    onReset();
    setLastStyleText(null);
    setExpanded(false);
  }

  function handleRedo() {
    onRedo();
    setLastStyleText(null);
  }

  const opsLabel =
    lastStyleText ?? (ops.length === 1 ? "1 styling change applied" : `${ops.length} styling changes applied`);

  const maxGroup =
    panel?.kind === "query" && panel.result.groups
      ? Math.max(...panel.result.groups.map((g) => Math.abs(g.value)), 0)
      : 0;
  const provenance =
    panel?.kind === "query" && panel.result.query
      ? queryProvenance(panel.result.query, panel.result.matchedRowCount ?? effectIds(panel.result).length, pointSet.count)
      : [];

  return (
    <div className="nlbar">
      {/* ------------------------------ result panel ------------------------ */}
      <div aria-live="polite">
        {panel?.kind === "query" && (
          <div className="nlbar__panel nlbar__panel--answer">
            <div className="nlbar__panel-head">
              <p className="nlbar__kicker">The answer</p>
              <IconButton label="Dismiss answer" variant="ghost" size="sm" onClick={dismissPanel}>
                <Icon icon={X} size="sm" />
              </IconButton>
            </div>
            <p className="nlbar__answer">{panel.result.answerText}</p>
            {panel.result.groups && panel.result.groups.length > 0 && (
              <div className="nlbar__groups">
                {[...panel.result.groups]
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 8)
                  .map((g) => (
                    <div key={g.group}>
                      <div className="nlbar__group-head">
                        <span className="nlbar__group-name">{g.group}</span>
                        <span className="nlbar__group-val">{formatValue(g.value)}</span>
                      </div>
                      <div className="nlbar__group-track">
                        <div
                          className="nlbar__group-fill"
                          style={{ width: `${maxGroup > 0 ? Math.max((Math.abs(g.value) / maxGroup) * 100, 2) : 0}%` }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {(provenance.length > 0 || panel.result.explanation || panel.result.anchor || effectIds(panel.result).length > 0) && (
              <>
                <hr className="nlbar__divider" />
                <p className="nlbar__kicker">How this was calculated</p>
                {provenance.length > 0 && (
                  <dl className="nlbar__provenance">
                    {provenance.map((item) => (
                      <div key={item.label} className="nlbar__provenance-row">
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {panel.result.explanation && (
                  <div className="nlbar__interpretation">
                    <p className="nlbar__kicker">How I read your question</p>
                    <p className="nlbar__echo">{panel.result.explanation}</p>
                  </div>
                )}
                {panel.result.anchor && (
                  <div className="nlbar__anchor-row">
                    <span className="nlbar__anchor">
                      <Icon icon={Navigation} size="sm" />
                      Anchored at {panel.result.anchor.label}
                      <span className="nlbar__anchor-coords">
                        {(anchorEdit?.lat ?? panel.result.anchor.lat).toFixed(4)},{" "}
                        {(anchorEdit?.lng ?? panel.result.anchor.lng).toFixed(4)}
                      </span>
                    </span>
                    {panel.result.query?.spatial && (
                      <span className="nlbar__nudge" role="group" aria-label="Correct the resolved anchor">
                        <span className="nlbar__nudge-axis">
                          <span className="nlbar__nudge-label">Lat</span>
                          <IconButton label="Nudge anchor north" variant="ghost" size="sm" onClick={() => nudgeAnchor(ANCHOR_NUDGE_STEP, 0)}>
                            <Icon icon={Plus} size="sm" />
                          </IconButton>
                          <IconButton label="Nudge anchor south" variant="ghost" size="sm" onClick={() => nudgeAnchor(-ANCHOR_NUDGE_STEP, 0)}>
                            <Icon icon={Minus} size="sm" />
                          </IconButton>
                        </span>
                        <span className="nlbar__nudge-axis">
                          <span className="nlbar__nudge-label">Lng</span>
                          <IconButton label="Nudge anchor east" variant="ghost" size="sm" onClick={() => nudgeAnchor(0, ANCHOR_NUDGE_STEP)}>
                            <Icon icon={Plus} size="sm" />
                          </IconButton>
                          <IconButton label="Nudge anchor west" variant="ghost" size="sm" onClick={() => nudgeAnchor(0, -ANCHOR_NUDGE_STEP)}>
                            <Icon icon={Minus} size="sm" />
                          </IconButton>
                        </span>
                      </span>
                    )}
                  </div>
                )}
                {effectIds(panel.result).length > 0 && (
                  <p className="nlbar__note">
                    <span className="nlbar__note-dot" aria-hidden />
                    {effectIds(panel.result).length.toLocaleString()} matching point
                    {effectIds(panel.result).length === 1 ? "" : "s"} on the map
                  </p>
                )}
              </>
            )}
            <div className="nlbar__answer-actions">
              <Button
                variant="secondary"
                size="sm"
                iconLeading={<Icon icon={Eye} size="sm" />}
                onClick={saveCurrentView}
                disabled={panel.saved}
              >
                {panel.saved ? "View saved" : "Save as view"}
              </Button>
            </div>
          </div>
        )}

        {panel?.kind === "clarify" && (
          <div className="nlbar__panel">
            <div className="nlbar__panel-head">
              <p className="nlbar__kicker">One quick check</p>
              <IconButton label="Dismiss question" variant="ghost" size="sm" onClick={dismissPanel}>
                <Icon icon={X} size="sm" />
              </IconButton>
            </div>
            <p className="nlbar__question">{panel.question}</p>
            <div className="nlbar__chips">
              {panel.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="nlbar__chip"
                  onClick={() => void submit(`${panel.prompt} — I mean ${option}`)}
                >
                  {option}
                </button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPanel(null);
                  inputRef.current?.focus();
                }}
              >
                Neither — let me rephrase
              </Button>
            </div>
          </div>
        )}

        {panel?.kind === "declined" && (
          <div className="nlbar__panel">
            <div className="nlbar__panel-head">
              <p className="nlbar__kicker">Not something I can do yet</p>
              <IconButton label="Dismiss" variant="ghost" size="sm" onClick={dismissPanel}>
                <Icon icon={X} size="sm" />
              </IconButton>
            </div>
            <div className="nlbar__status">
              <span className="nlbar__status-icon">
                <Icon icon={Info} size="md" />
              </span>
              <div className="nlbar__status-body">
                <p className="nlbar__status-text">{panel.text}</p>
                <p className="nlbar__status-sub">The manual style and filter controls do everything, just by hand.</p>
              </div>
            </div>
            {panel.fallbacks.length > 0 && (
              <div className="nlbar__chips">
                {panel.fallbacks.slice(0, 3).map((f) => (
                  <button
                    key={f}
                    type="button"
                    className="nlbar__chip"
                    onClick={() => {
                      setInput(f);
                      setPanel(null);
                      inputRef.current?.focus();
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {panel?.kind === "degraded" && (
          <div className="nlbar__panel">
            <div className="nlbar__panel-head">
              <p className="nlbar__kicker">Questions are resting</p>
              <IconButton label="Dismiss" variant="ghost" size="sm" onClick={dismissPanel}>
                <Icon icon={X} size="sm" />
              </IconButton>
            </div>
            <div className="nlbar__status">
              <span className="nlbar__status-icon">
                <Icon icon={Info} size="md" />
              </span>
              <div className="nlbar__status-body">
                <p className="nlbar__status-text">{panel.text}</p>
                <p className="nlbar__status-sub">
                  Your map is untouched, and every manual control still works — style, filter, and cluster from the
                  side panel.
                </p>
              </div>
            </div>
          </div>
        )}

        {panel?.kind === "error" && (
          <div className="nlbar__panel">
            <div className="nlbar__panel-head">
              <p className="nlbar__kicker">Hit a snag</p>
              <IconButton label="Dismiss" variant="ghost" size="sm" onClick={dismissPanel}>
                <Icon icon={X} size="sm" />
              </IconButton>
            </div>
            <div className="nlbar__status nlbar__status--danger">
              <span className="nlbar__status-icon">
                <Icon icon={CircleAlert} size="md" />
              </span>
              <div className="nlbar__status-body">
                <p className="nlbar__status-text">{panel.text}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* -------------------- style confirmation + "What I did" ------------- */}
      {(ops.length > 0 || canRedo) && (
        <div className={cx("nlbar__ops", expanded && "nlbar__ops--open")}>
          <div className="nlbar__ops-row">
            <span className="nlbar__ops-check" aria-hidden>
              <Icon icon={Check} size="sm" />
            </span>
            <span className="nlbar__ops-text">{ops.length > 0 ? opsLabel : "Change undone"}</span>
            {ops.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                iconLeading={<Icon icon={RotateCcw} size="sm" />}
                onClick={handleUndo}
              >
                Undo
              </Button>
            )}
            {canRedo && (
              <Button variant="secondary" size="sm" onClick={handleRedo}>
                Redo
              </Button>
            )}
            {ops.length > 0 && (
              <IconButton
                label={expanded ? "Hide what I did" : "Show what I did"}
                variant="ghost"
                size="sm"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                <span className="nlbar__ops-chevron">
                  <Icon icon={ChevronDown} size="sm" />
                </span>
              </IconButton>
            )}
          </div>
          {expanded && (
            <div className="nlbar__ops-body">
              <div className="nlbar__ops-head">
                <p className="nlbar__ops-title">What I did</p>
                <span className="nlbar__readonly">read-only</span>
              </div>
              <ol className="nlbar__ops-list">
                {ops.map((op, i) => (
                  <li key={`${i}-${op.type}`} className="nlbar__op">
                    <span className="nlbar__op-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="nlbar__op-type">{op.type}</span>
                    <span className="nlbar__op-args">{op.confirmationText ?? opArgs(op)}</span>
                  </li>
                ))}
              </ol>
              <div className="nlbar__ops-foot">
                <p className="nlbar__ops-note">Every change is a small, inspectable op — Undo rewinds exactly one.</p>
                <Button variant="ghost" size="sm" onClick={handleReset}>
                  Reset all
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* -------------------------------- the bar --------------------------- */}
      <form
        className="nlbar__bar"
        onSubmit={(e) => {
          e.preventDefault();
          void submit(input);
        }}
      >
        <Spark />
        <div className="nlbar__field">
          {input === "" && (
            <span key={hintIdx} className={cx("nlbar__hint", hintIdx > 0 && "nlbar__hint--animated")} aria-hidden>
              {hint}
            </span>
          )}
          <input
            ref={inputRef}
            className="nlbar__input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Ask about your data, or say how to style the map"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
          />
        </div>
        <button type="submit" className="nlbar__send" disabled={busy} aria-label="Send">
          {busy ? <Spinner size="sm" tone="inverse" /> : <Icon icon={ArrowRight} size="md" />}
        </button>
      </form>
    </div>
  );
}
